import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TableContainer,
  Paper,
  Box,
  CircularProgress,
  Button,
} from "@mui/material";
import { WorkflowData, initialWorkflowData } from "../../types/Workflow";
import {
  getData,
  createData,
  updateData,
  deleteData,
} from "../../api/WorkflowApi";

import { getData as getTransactionData } from "../../api/TransactionApi";
import { AddCircleSharp } from "@mui/icons-material";
import { ListData } from "../../types/List";
import { toast } from "react-toastify";
import { showConfirmation } from "../../utils/SwalHelper";
import WorkflowForm from "./WorkflowForm";
import { hasModuleActionPermission } from "../../utils/RolePermissions";
import { AppActionEnum, ModuleEnum } from "../../utils/AppEnum";
import { GenericTable } from "../../components/common/GenericTable";
import { getWorkflowTableColumns } from "./table/ColumnHeader";
import WorkflowDiagram from "./WorkflowDiagram";

const WorkflowList: React.FC = () => {
  const [inputData, setInputData] = useState<WorkflowData>(initialWorkflowData);
  const [listData, setListData] = useState<WorkflowData[]>([]);
  const [transactionData, setTransactionData] = useState<ListData[]>([]);

  const [open, setOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [showDiagram, setShowDiagram] = useState(false);
  const [loading, setLoading] = useState(true); // <-- loading state

  const loadData = async () => {
    setLoading(true);

    const transactionResp = await getTransactionData();
    if (transactionResp == null) {
      toast.error("Failed to get list of transactions");
      return;
    }
    await setTransactionData(transactionResp);

    const result = await getData();
    if (result == null) {
      toast.error("Failed to get list of workflows");
      return;
    }
    await setListData(result);

    setLoading(false);
  };

  const refreshData = async () => {
    setLoading(true);

    const result = await getData();
    if (result == null) {
      toast.error("Failed to refresh");
      return;
    }
    await setListData(result);
    setLoading(false);
  };

  const handleAdd = async () => {
    setIsEdit(false);

    await setInputData(initialWorkflowData);

    setOpen(true);
  };
  const handleEdit = async (data: WorkflowData) => {
    setIsEdit(true);

    await setInputData(data);

    setOpen(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const confirmed = await showConfirmation(
        "Are you sure?",
        "Do you want to delete this record permanently?",
        "warning",
        "Yes, delete it!"
      );

      if (confirmed) {
        const resp = await deleteData(id);
        if (!resp) {
          toast.error("Failed to delete record");
          return;
        }
        refreshData();
        toast.success("Record successfully deleted.");
      }
    } catch (error: any) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.message || "Error deleting record");
    }
  };

  const handleShowDiagram = async (data: WorkflowData) => {
    setInputData(data);
    await setShowDiagram(true);
  };

  const handleBack = async () => {
    await setShowDiagram(false);
  };
  const handleSubmit = async (data: Partial<WorkflowData>) => {
    setIsEdit(false);

    if (data?.id) {
      const resp = await updateData(data.id, data);
      if (resp == null) {
        toast.error("Update failed.");
        return;
      }

      toast.success("Record successfully updated.");
    } else {
      const resp = await createData(data);

      if (resp == null) {
        toast.error("Failed to create record");
        return;
      }
      toast.success("Record successfully created.");
    }
    setOpen(false);
    refreshData();
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div>
      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !showDiagram && (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" }, // column on mobile
              alignItems: { xs: "stretch", md: "center" },
              gap: 2,
              width: "100%",
            }}
          >
            {/* GenericSearch */}
            <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 90%" } }}></Box>

            {/* Add Button */}
            {hasModuleActionPermission(
              ModuleEnum.Registration,
              AppActionEnum.Create
            ) && (
              <Box sx={{ flex: { xs: "0 0 100%", md: "0 0 8%" } }}>
                <Button
                  fullWidth
                  startIcon={<AddCircleSharp />}
                  onClick={handleAdd}
                  variant="contained"
                  color="primary"
                >
                  Add
                </Button>
              </Box>
            )}
          </Box>

          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <GenericTable
              data={listData}
              columns={getWorkflowTableColumns}
              actions={{
                edit: (row) => handleEdit(row),
                delete: (row) => handleDelete(row.id),
                view: (row) => handleShowDiagram(row),
              }}
            />
          </TableContainer>
          <Dialog
            open={open}
            onClose={(event, reason) => {
              if (reason === "backdropClick" || reason === "escapeKeyDown")
                return;
              setInputData(initialWorkflowData); // reset only when dialog truly closes
              setOpen(false);
            }}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              {isEdit ? "Edit Workflow" : "Add Workflow"}
            </DialogTitle>
            <DialogContent>
              <WorkflowForm
                onSubmit={handleSubmit}
                initialData={inputData || initialWorkflowData}
                onClose={() => {
                  setInputData(initialWorkflowData);
                  setOpen(false);
                }}
                onBack={handleBack}
                transactionData={transactionData}
              />
            </DialogContent>
          </Dialog>
        </>
      )}

      {!loading && showDiagram && !!inputData.name && (
        <>
          <WorkflowDiagram workflow={inputData} onBack={handleBack} />
        </>
      )}
    </div>
  );
};

export default WorkflowList;
